import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import { APP_INITIALIZER, LOCALE_ID, inject } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import {
  I18NEXT_NAMESPACE_RESOLVER,
  I18NEXT_SERVICE,
  ITranslationService,
  defaultInterpolationFormat,
  provideI18Next
} from 'angular-i18next';
import Backend from 'i18next-http-backend';
import moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { FORMAT_TOKEN, formatTitle } from './i18-format-title';

const LOCALE_DATA = {
  en: localeEn
};

/**
 * Detect lang be html lang="" attr async
 * @return {Observable<string>}
 */
export function documentLangObserver(): Observable<string> {
  const subject: Subject<string> = new Subject();
  const mutationObserver = new MutationObserver((mutationsList, _observer) => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'lang') {
        subject.next((mutation.target as HTMLElement).getAttribute('lang'));
      }
    }
  });

  mutationObserver.observe(document.documentElement, { attributes: true });
  return subject.pipe(startWith(document.documentElement.getAttribute('lang')));
}
/**
 * Lang detect ngApp integration
 */
const LANGUAGE_DETECTOR = {
  type: 'languageDetector',
  async: true,
  init: function (services, detectorOptions, i18nextOptions) {
    /* use services and options */
  },
  detect: function (callback) {
    documentLangObserver().subscribe((lang) => {
      registerLocaleData(LOCALE_DATA[lang] || LOCALE_DATA.en, lang);
      callback(lang);
    });
  },
  cacheUserLanguage: function (lng) {
    /* cache language */
  }
};

function getI18nextOptions(path: string, namespaces?: Array<string>, defaultNS?: string) {
  return {
    whitelist: ['en'],
    fallbackLng: 'en',
    debug: false,
    returnEmptyString: true,
    defaultNS,
    interpolation: {
      format: (value, format, lng) => {
        if (format === FORMAT_TOKEN) return formatTitle(value, lng);
        if (value instanceof Date) return moment(value).format(format);

        return defaultInterpolationFormat(value, format, lng);
      }
    },
    backend: {
      loadPath: path,
      addPath: path
    },
    ns: namespaces
  };
}

// Standalone equivalent of the I18NEXT_NAMESPACE_RESOLVER provided by I18NextModule.forRoot().
// provideI18Next() does not include it, so we provide it manually here.
function i18nextNamespaceResolver(activatedRouteSnapshot: ActivatedRouteSnapshot) {
  const i18next = inject(I18NEXT_SERVICE);
  const namespaces: string[] = activatedRouteSnapshot.data?.['i18nextNamespaces'] ?? [];
  return i18next.loadNamespaces(namespaces.filter((n) => n));
}

function i18NextInit(i18next: ITranslationService, path: string, namespaces: Array<string>, defaultNS?: string) {
  return () =>
    i18next
      .use<any>(Backend)
      .use<any>(LANGUAGE_DETECTOR)
      .init(getI18nextOptions(path, namespaces, defaultNS));
}

/*
 * get i18next instance ID
 */
export function localeIdFactory(i18next: ITranslationService) {
  return i18next.language;
}

export function I18N_PROVIDERS(path: string, namespaces?: Array<string>, defaultNS = 'app') {
  return [
    provideI18Next(),
    {
      provide: I18NEXT_NAMESPACE_RESOLVER,
      useValue: i18nextNamespaceResolver
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (i18next: ITranslationService) => i18NextInit(i18next, path, namespaces, defaultNS),
      deps: [I18NEXT_SERVICE],
      multi: true
    },
    {
      provide: LOCALE_ID,
      deps: [I18NEXT_SERVICE],
      useFactory: localeIdFactory
    }
  ];
}
